/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useMemo, useRef, useState } from "react"
import Map from "@components/general/Map";
import { MapType } from "@config/appConfig";
import { IExtLocation, IFlightPoint, ILocation, IOriginDestinationRequest, IRoute, LeafletLayerName, Locality, TripType } from "@common/models";
import Button from "@components/general/form/Button";
import Translate from "@service/Translate";
import { IAppState } from "@misc/State";
import { IWeekSchedule } from "@common/models/CMS/WeekSchedule";
import { Culture } from "@core/Culture";
import { setFirstAvailableDate, totalWeeklyFlights } from "@components/FeaturedFlights";
import { IconOptions } from "leaflet";
import airportArrival from '@assets/images/icons/marker-arrival.svg';
import airportDeparture from '@assets/images/icons/marker-departure.svg';
import marker from '@assets/images/icons/marker.svg';
import lShadow from '@assets/images/icons/office-marker-shadow.png';
import WeeklyFlightAvailability from "@components/general/WeeklyFlightAvailability";
import { getRoute } from "./CityInformation";
import { useSelector } from "react-redux";
import { RoutePath } from "@common/enum/RoutePath";
interface IRouteDemonstration {
    areaPoint: ILocation[];
    routeState: Locality;
    leafletLayerName?: LeafletLayerName;
}
interface IFeatureRoute extends IFlightPoint {
    originDestinationRequest?: IOriginDestinationRequest;
}

interface IIndex { index: number, continueInterval: boolean }

interface timeProperty {
    indexRegion: number,
    timeRatio: number,
    timeDuration: number,
    defaultTime: number
}

interface IDataFeed extends IFlightPoint {
    destinations: [IFeatureRoute, IWeekSchedule][],
    tripType?: TripType;
}

let timeOnAsideDuration: number = 0;
let routeDemonstrationTimer: NodeJS.Timeout;
const RouteDemonstration: FC<IRouteDemonstration> = ({ areaPoint, routeState, leafletLayerName }) => {
    const { tr } = Translate;
    const regionOfResponsive = 1200;
    const flightSelected = useRef<HTMLElement>(null)
    const panelElement = useRef<HTMLElement>(null)
    const locale = Culture.getLocale();
    let { routes, airports, width } = useSelector((state: IAppState) => (
        {
            routes: state.routes,
            airports: state.airports,
            availabilityRequest: state.availabilityRequest,
            width: state.windowWidth
        }));
    const [activeMap, setActiveMap] = useState<boolean>(false);
    const [originCities, setOriginCities] = useState<(IFlightPoint)[]>();
    const [dataFeed, setDataFeed] = useState<IDataFeed[]>();
    const [polyLineData, setPolyLineData] = useState<ILocation[][]>();
    const [markersPoint, setMarkersPoint] = useState<IExtLocation[]>();
    const [indexState, setIndex] = useState<IIndex>({ index: 0, continueInterval: true });
    const [timeOfInterval, setTimeOfInterval] = useState<number>();
    const [scrollDone, setScrollDone] = useState<boolean>(true);
    const [expand, setExpand] = useState<boolean>();
    const timeProperty: timeProperty = {
        indexRegion: 2,
        timeDuration: 1500,
        timeRatio: 800,
        defaultTime: 5000
    }
    routes = useMemo(() => routes.filter(ro => ro.locality === routeState && ro.path !== RoutePath[RoutePath.transit]), [routes]);

    useEffect(() => {
        if (routes) {
            clearInterval(routeDemonstrationTimer)
            setOriginCities(convertAirPortsToCityName(routes, airports) as IFlightPoint[]);
            setTimeout(() => setPolyLineData(setInitialPolyLine(routes, airports)), 700)
        }
    }, [routes, airports]);

    useEffect(() => {
        setIndex({
            index: 0, continueInterval: true
        })
    }, [width])

    useEffect(() => {
        const dataGenerated = originCities?.map(
            // eslint-disable-next-line array-callback-return
            item => {
                if (item?.code) {
                    return {
                        ...item,
                        destinations: getDestinations(routes, item?.code, airports).filter(des => des[0]?.latitude && des[0]?.longitude)
                    }
                }
            }
        ) as IDataFeed[]
        const dataSorted = dataGenerated?.sort((value1, value2) =>
            computeNumberOfFlightsOfEachCity(value1) === computeNumberOfFlightsOfEachCity(value2) ?
                -(value1.destinations.length - value2.destinations.length)
                :
                -(computeNumberOfFlightsOfEachCity(value1) - computeNumberOfFlightsOfEachCity(value2)));
        setDataFeed(dataSorted?.filter(data => data?.latitude && data?.longitude));
    }, [originCities]);

    useEffect(() => {
        if (dataFeed) {
            setMarkersPoint(convertToStandardMapDataModel(dataFeed as IDataFeed[]));
            setSlideShow();
            setTimeOfInterval(computeTimeOfInterval(dataFeed, indexState.index, timeProperty));
        }
    }, [dataFeed])

    useEffect(() => {
        if (dataFeed) {
            const time = computeTimeOfInterval(dataFeed, indexState.index, timeProperty);
            const initTime = dataFeed[0]?.destinations?.length * 800 + 600
            setTimeOfInterval(dataFeed.length === indexState.index ? initTime : time)
        }
    }, [flightSelected.current])

    useEffect(() => {
        if (dataFeed) {
            setMarkersPoint(passingMarkerOfEachFlight(dataFeed[indexState.index - 1]))
            setPolyLineData(passingLocationOfEachFlight(dataFeed[indexState.index - 1]))
        }
        const target = flightSelected.current as HTMLElement;
        const top = target?.offsetTop;
        if (scrollDone) panelElement?.current?.scrollTo(0, top)
        if (indexState.continueInterval) {
            clearInterval(routeDemonstrationTimer)
            setSlideShow()
        }
        setListenerOnMarker()

    }, [indexState.index])

    useEffect(() => {
        return () => {
            clearInterval(routeDemonstrationTimer)
        }
    }, [])

    const setSlideShow = (setIndexStatus = true, deltaAsideTime?: number) => {
        if (dataFeed) {
            let index = indexState.index;
            let time = computeTimeOfInterval(dataFeed, index - 1, timeProperty);
            time = deltaAsideTime ? deltaAsideTime >= time ? timeProperty.timeDuration : time - deltaAsideTime : time;
            routeDemonstrationTimer = setInterval(() => {
                if (dataFeed[0]) {
                    index += 1;
                    if (setIndexStatus) setIndex({ index: index, continueInterval: true })
                }
                if (index === dataFeed.length + 1 && dataFeed.length > 1) {
                    index = 0
                    setIndex({ index: 1, continueInterval: true })
                }
            }, (indexState.index > 0) ? time : 3500);
        }
    }

    const activatingMap = (): void => {
        setActiveMap(!activeMap);
        if (!activeMap) {
            setIndex({ index: 0, continueInterval: false })
            if (routeDemonstrationTimer) clearInterval(routeDemonstrationTimer)
        }
        else {
            setTimeout(() => setIndex({ index: 0, continueInterval: true }), 250)
            setSlideShow()
        }
    };

    const convertAirPortsToCityName = (routes: IRoute[], airports: IFlightPoint[], mode: 'org' | 'des' = 'org'): IFlightPoint[] | [IFeatureRoute, IWeekSchedule][] => {
        if (mode === 'org') {
            const improvedRoutes = removeRepeatedCitiesAndSeparateFlights(routes);
            const result = improvedRoutes.map(item => {
                const airPortPropertyOfOrigin = convertRouteToAirportsProperty(item, airports);
                return airPortPropertyOfOrigin;
            })
            return result;
        } else {
            const result = routes.map(item => {
                const airPortPropertyOfDestinations = convertRouteToAirportsProperty(item, airports, 'des');
                const featuredFlight = {
                    ...airPortPropertyOfDestinations,
                    originDestinationRequest: {
                        originCode: item.origin,
                        destinationCode: airPortPropertyOfDestinations?.cityCode,
                        departureDate: setFirstAvailableDate(item?.weekSchedule),
                    }
                }
                return [featuredFlight, item.weekSchedule];
            })
            return result as [IFeatureRoute, IWeekSchedule][];
        }
    }

    const getDestinations = (mainRoutes: IRoute[], airPortCode: string, airports: IFlightPoint[]): [IFeatureRoute, IWeekSchedule][] => {
        const improveRoutes = mainRoutes?.filter(route => route.origin === airPortCode);
        const result = convertAirPortsToCityName(improveRoutes, airports, 'des') as [IFeatureRoute, IWeekSchedule][];
        return result.sort((item1, item2) => {
            return (-(totalWeeklyFlights(item1[1]) - totalWeeklyFlights(item2[1])))
        })
    }

    const setInitialPolyLine = (routes: IRoute[], airports: IFlightPoint[]): IExtLocation[][] => {
        const polyLines: IExtLocation[][] = [];
        routes?.forEach(item => {
            const origin = airports.find(airport => airport.code === item.origin);
            const destination = airports.find(airport => airport.code === item.destination);
            polyLines.push(
                [{
                    lat: origin?.latitude as number,
                    lng: origin?.longitude as number
                }, {
                    lat: destination?.latitude as number,
                    lng: destination?.longitude as number
                }]
            )
        })
        return polyLines
    }

    const setListenerOnMarker = (): void => {
        setTimeout(() => {
            const markerContainerElement = document.querySelector('.leaflet-pane .leaflet-marker-pane');
            const markersTarget = markerContainerElement?.children;
            if (markersTarget && dataFeed) {
                const markersElement = convertingNodesToArray(markersTarget)
                markersElement.forEach((element, index) => {
                    const setIndexMarker = index > dataFeed.length ? dataFeed.indexOf(dataFeed.find(item => item.cityName === (element as HTMLElement).title) as IDataFeed) : index;
                    element.addEventListener('click', () => {
                        const time = computeTimeOfInterval(dataFeed, index, timeProperty)
                        setTimeOfInterval(time)
                        setIndex({ index: setIndexMarker + 1, continueInterval: false });
                        if (routeDemonstrationTimer) clearInterval(routeDemonstrationTimer);
                        if (width < regionOfResponsive) window.scrollTo({ top: 0, behavior: 'smooth' })
                    })
                })
            }
        }, 100)
    }

    const passingMarkerOfEachFlight = (flight: IDataFeed): IExtLocation[] | undefined => {
        const result: IExtLocation[] = [{
            lat: flight?.latitude as number,
            lng: flight?.longitude as number,
            icon: setIconForMarkers(airportDeparture),
            content: { contentTitle: flight?.cityName as string, container: flight?.cityName as string },
            popupConfig: {
                openPopup: true,
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
                autoPan: false,
                autoPanPadding: [0, 0],
                popupDelay: !activeMap ? 600 : undefined
            }
        }]
        flight?.destinations.forEach(item => {
            result.push({
                lat: item[0]?.latitude as number,
                lng: item[0]?.longitude as number,
                icon: setIconForMarkers(airportArrival),
                content: { contentTitle: item[0]?.cityName as string, container: item[0]?.cityName as string },
                popupConfig: {
                    openPopup: true,
                    autoClose: false,
                    popupDelay: !activeMap ? 600 : undefined,
                    closeButton: false,
                    closeOnClick: false,
                    autoPan: false,
                    autoPanPadding: [0, 0]
                }
            })
        })
        return result
    }

    const daysOfTheWeek = locale.weekDays.map(day =>
        <h4 className='day-title'>
            {day.abbreviation}
        </h4>

    )

    const routeItems = dataFeed ? dataFeed.map((airport, index) =>
        <section
            className={(index + 1 === indexState.index) ? Locality.Domestic ? 'item-container selected-domestic' : 'item-container selected-international' : 'item-container'}
            ref={(index + 1 === indexState.index) ? flightSelected : undefined}
            onMouseOver={() => {
                if (width > regionOfResponsive) {
                    const validIndex = !(indexState.index === index + 1);
                    if (validIndex) {
                        setIndex({ index: index + 1, continueInterval: true });
                        setScrollDone(false);
                    }
                }
            }}
            onMouseEnter={() => {
                const currentTime = Number(new Date());
                timeOnAsideDuration = currentTime;
            }
            }
            onMouseOut={() => {
                if (width > regionOfResponsive)
                    setScrollDone(true);
            }}
            key={'rd-aside-item-' + index}>
            <header className='item-header-container'>
                {
                    width < regionOfResponsive ?
                        <div
                            className="progress-interval"
                            style={
                                indexState.continueInterval ?
                                    {
                                        transition: `${timeOfInterval as number / 1000}s cubic-bezier(0.2, 0.4, 0.85, 0.65)`
                                    } :
                                    {
                                        display: 'none'
                                    }
                            }
                        /> : undefined}
                <h3 className='header-title'>
                    {tr('of', airport?.cityName)}
                </h3>
                <h3 className='header-title'>
                    {tr('rd_n_flights_to_m_destinations', computeNumberOfFlightsOfEachCity(airport), airport?.destinations.length)}
                </h3>
                <Button
                    className="expand-btn"
                    key='rd-btn-responsive'
                    onClick={() => {
                        setExpand(!expand);
                        clearInterval(routeDemonstrationTimer);
                        setTimeout(() => {
                            const target = flightSelected.current as HTMLElement;
                            const top = target?.offsetTop;
                            if (panelElement.current)
                                // scrollTopAnalyse()
                                panelElement?.current?.scrollTo(0, top);
                        }, 250)
                    }
                    }
                    icon="icon-chevron-down"
                    text={tr('rd_indicate_weekly_flights')}
                    color='primary'
                    variant='outlined'
                />
            </header>
            <section className='title-days'>
                {daysOfTheWeek}
            </section>
            <ul className='item-article'>
                {airport?.destinations
                    .map(des =>
                        <li className='list-item trim'>
                            <h5 className='destination-name'>
                                {des[0]?.cityName}
                            </h5>
                            <WeeklyFlightAvailability route={getRoute(routes, { desCode: des[0].code as string, orgCode: airport.code as string }) as IRoute} weekDaysTitleVisiblity={false} destinationVisiblity={false} />
                        </li>
                    )
                }
            </ul>
        </section>
    ) : ''

    return (
        <main className='route-demonstration' key='rd-main'>
            <aside
                key='rd-aside'
                onMouseOver={() => { if (routeDemonstrationTimer && width > regionOfResponsive) clearInterval(routeDemonstrationTimer) }}
                onMouseLeave={() => {
                    if (!activeMap && width > regionOfResponsive) {
                        const currentTime = Number(new Date());
                        timeOnAsideDuration && setSlideShow(true, currentTime - timeOnAsideDuration);
                    }
                }
                }
                className={`main-aside ${expand ? 'expand' : ''} ${indexState.index === 0 ? ' de-active' : ' in-Active'}`} ref={panelElement}>
                <section className={(indexState.index === 0 && !activeMap) ? 'aside-title' : 'aside-title hide'}>
                    <div className="aside-title-text">
                        {routeState === Locality.Domestic ? tr('domestic_flights') : tr('international_flights')}
                    </div>
                    <div className="aside-title-text">
                        {
                            tr('rd_n_flights_to_m_destinations', routes?.filter(
                                route => route.locality === routeState)?.length, originCities?.length
                            )
                        }
                    </div>
                </section>
                {routeItems}
                <Button
                    className="collapse-btn"
                    key='rd-btn-responsive'
                    onClick={() => {
                        if (dataFeed) {
                            const time = computeTimeOfInterval(dataFeed, indexState.index, timeProperty);
                            setTimeOfInterval(time)
                        }
                        setTimeout(() =>
                            setIndex((previewState) => ({ index: dataFeed?.length === previewState.index ? 1 : previewState.index + 1, continueInterval: true }))
                            , 500)
                        setExpand(!expand);
                        setSlideShow();
                    }}
                    icon="icon-chevron-up"
                    text={tr('rd_return_map')}
                    color='primary'
                    variant='contained'
                />
            </aside>
            <article
                key='rd-article'
                className={'main-article' + (activeMap ? ' active' : '') + (expand ? ' hide-map' : '')}>
                <Map
                    mode={MapType.Leaflet}
                    leafletMapOption={{
                        initial: {
                            doubleClickZoom: false,
                            dragging: false,
                            scrollWheelZoom: false,
                            touchZoom: false,
                            trackResize: false,
                        },
                        current: {
                            boxZoom: activeMap,
                            doubleClickZoom: activeMap,
                            dragging: activeMap,
                            scrollWheelZoom: activeMap,
                            touchZoom: activeMap,
                        }
                    }}
                    leafletLayerName={leafletLayerName}
                    height={width < regionOfResponsive ? '100%' : 'calc(100vh - 80px)'}
                    areaPoints={activeMap ? undefined : areaPoint}
                    markers={markersPoint}
                    transitionActivate={!activeMap}
                    polyline={{
                        paths: polyLineData?.filter(path => path.every(loc => loc.lat && loc.lng)),
                        polyLineOption: indexState.index === 0 ? { color: '#00a69338', smoothFactor: 25, weight: 3 } : { color: '#00a693', smoothFactor: 25, weight: 2 },
                        animationForLine: !activeMap,
                        delayForDrowningInEachGroup: indexState.index === 0 ? 0 : 600,
                        transitionTime: 1
                    }}
                    markerAnimationProperty={activeMap ? undefined : {
                        animationPropertyAtStart: { opacity: '0' },
                        animationPropertyAtEnd: { opacity: '1' },
                        keyAttribute: 'src',
                        valueAttribute: airportArrival,
                    }}
                    popupStyle={
                        {
                            popupStyle: {
                                color: '#00a693',
                                fontWeight: 'bolder',
                            },
                            popupIndex: 0,
                        }
                    }
                />
            </article>
            <Button
                className={"map-btn" + (expand ? ' no-display' : '')}
                key='rd-btn'
                onClick={activatingMap}
                text={activeMap ? tr('map_disable') : tr('map_usage')}
                color='primary'
                variant='contained'
            />
        </main>
    )
}

const removeRepeatedCitiesAndSeparateFlights = (routes: IRoute[]): IRoute[] => {
    let improveRoutes: IRoute[] = [];
    routes.forEach(so => {
        const citiesCode = so.origin;
        if (!improveRoutes.find(co => co.origin === citiesCode))
            improveRoutes.push(so as IRoute);
    })
    return improveRoutes
}

const convertRouteToAirportsProperty = (city: IRoute, airports: IFlightPoint[], mode: 'org' | 'des' = 'org'): IFlightPoint => {
    switch (mode) {
        case 'des':
            const airPort1 = airports.find(item => item.code === city.destination) as IFlightPoint;
            return airPort1;
        default:
            const airPort = airports.find(item => item.code === city.origin) as IFlightPoint;
            return airPort;
    }

}

const computeNumberOfFlightsOfEachCity = (flight: IDataFeed): number => {
    let countNumberOfFlight = 0;
    flight?.destinations?.forEach(item => {
        countNumberOfFlight += totalWeeklyFlights(item[1])
    })
    return countNumberOfFlight
}

const convertToStandardMapDataModel = (data: IDataFeed[]): IExtLocation[] => {
    const result: IExtLocation[] = data?.map(
        item => {
            return {
                lat: item?.latitude as number,
                lng: item?.longitude as number,
                title: item?.otherAirportName,
                icon: setIconForMarkers(marker),
                remainedState: true,
            }
        }
    )
    return result
}

const passingLocationOfEachFlight = (flight: IDataFeed): ILocation[][] | undefined => {
    const origin: ILocation = {
        lat: flight?.latitude as number,
        lng: flight?.longitude as number
    }
    const result: ILocation[][] = flight?.destinations.map(item => {
        return [origin, {
            lat: item[0]?.latitude as number,
            lng: item[0]?.longitude as number
        }]
    })
    return result
}

export const convertingNodesToArray = <TNode extends Node>(nodes: HTMLCollection | NodeListOf<TNode>): (Element | TNode)[] => {
    const result: (Element | TNode)[] = []
    for (let indexElement = 0; indexElement < nodes.length; indexElement++) {
        result.push(nodes[indexElement] as Element | TNode)
    }
    return result
}

export const setIconForMarkers = (icon: string): IconOptions => {
    const iconConfig: IconOptions = {
        iconUrl: icon,
        shadowUrl: lShadow,
        iconSize: [40, 40],
        shadowSize: [40, 40],
        iconAnchor: [20, 40],
        shadowAnchor: [14, 40],
        popupAnchor: [0, -45]
    }
    return iconConfig
}

const computeTimeOfInterval = (dataFeed: IDataFeed[], index: number, timeOption: timeProperty): number => {
    const timeRatioForTehran = 650;
    const time = dataFeed[index]?.destinations.length > timeOption.indexRegion ?
        dataFeed[index]?.code === "THR" ?
            dataFeed[index]?.destinations?.length * timeRatioForTehran + timeOption.timeDuration
            :
            dataFeed[index]?.destinations?.length * timeOption.timeRatio + timeOption.timeDuration
        :
        timeOption.defaultTime;
    return time
}

// const scrollTopAnalyse = (): boolean => {
//     const element = document.querySelector('.main-aside')! as HTMLElement;
//     const scrollValue: number = element.scrollTop;
//     element.scrollTo(0, scrollValue + 1);
//     const newScrollValue = element.scrollTop;
//     return (newScrollValue === scrollValue)
// }

export default RouteDemonstration

